// PRD-010 T3.2 (GREEN) — CommandSerializer: single source of truth for
// serializing internal package commands into the public
// `SerializedWhiteboardCommand` discriminated union dispatched via the
// `whiteboard-command` CustomEvent.
//
// Consolidates the 9 `instanceof` arms previously in
// `serialize-command.helper.ts` PLUS an intentional 10th arm for
// `SetListTypeCommand` so the closed `WhiteboardCommand` union is honest
// about every command type currently in the package. The 10th arm
// returns `null` by design — its serialized shape is a textbox internal
// that consumers do not (yet) need, and adding it to the public
// `SerializedWhiteboardCommand` union would violate PRD-010 Failure
// Condition #5 (no public type signature changes).
//
// Exhaustiveness: the dispatcher narrows `cmd` to `never` after the last
// `instanceof` arm. Adding a new variant to `WhiteboardCommand` without
// a matching arm yields TS2322 on the `_exhaustive` assignment.

import type {
  SerializedWhiteboardCommand,
  SerializedMove,
} from '../../elements/whiteboard-command.event.js';
import { mirrorLineSwap } from '../../features/line/line-swap-utils.js';
import { AddObjectCommand } from './add-object.command.js';
import { RemoveObjectCommand } from './remove-object.command.js';
import { RemoveObjectWithAnchorsCommand } from './remove-object-with-anchors.command.js';
import { MoveObjectsCommand } from './move-objects.command.js';
import { NudgeObjectsCommand } from './nudge-objects.command.js';
import { ResizeObjectCommand } from './resize-object.command.js';
import { UpdatePropertyCommand } from './update-property.command.js';
import { DuplicateObjectsCommand } from './duplicate-objects.command.js';
import { ZOrderCommand } from './z-order.command.js';
import { SwapLineCapsCommand } from '../../features/line/line.commands.js';
import { SetListTypeCommand } from '../../features/textbox/textbox.commands.js';
import { ApplyWholeTextFormatCommand } from './apply-whole-text-format.command.js';

/**
 * Extras context that cannot be derived from the command instance alone:
 *  - `sourceUids` for `DuplicateObjectsCommand` (captured at the call
 *    site before push — see PRD-009 Open Questions, resolved (b)).
 *  - `toIndex` for `ZOrderCommand` (must be read from
 *    `boardState.getObjectIndex(objectUid)` AFTER `history.push()`
 *    returns — see PRD-009 §Technical Requirements).
 */
export interface SerializeCommandExtras {
  readonly sourceUids?: ReadonlyArray<string>;
  readonly toIndex?: number;
}

/**
 * Closed union of every command type currently produced inside the
 * whiteboard package. Required for TypeScript exhaustiveness narrowing
 * via `const _exhaustive: never = cmd` — an open `Command<BoardState>`
 * cannot be narrowed to `never` after `instanceof` checks.
 *
 * Adding a new command type? Add it here AND add a matching
 * `instanceof` arm in `CommandSerializer.serialize()`. The TS2322
 * compile error on `_exhaustive` is the safety net.
 */
export type WhiteboardCommand =
  | AddObjectCommand
  | RemoveObjectWithAnchorsCommand
  | RemoveObjectCommand
  | MoveObjectsCommand
  | NudgeObjectsCommand
  | ResizeObjectCommand
  | UpdatePropertyCommand
  | DuplicateObjectsCommand
  | ZOrderCommand
  | SwapLineCapsCommand
  | SetListTypeCommand
  | ApplyWholeTextFormatCommand;

export class CommandSerializer {
  static serialize(
    cmd: WhiteboardCommand,
    extras?: SerializeCommandExtras,
  ): SerializedWhiteboardCommand | null {
    if (cmd instanceof AddObjectCommand) {
      return { kind: 'add-object', objectUid: cmd.object.id, payload: cmd.object };
    }
    if (cmd instanceof RemoveObjectWithAnchorsCommand) {
      if (cmd.ids.length === 0) return null;
      return { kind: 'remove-object', objectUids: [...cmd.ids] };
    }
    if (cmd instanceof RemoveObjectCommand) {
      return { kind: 'remove-object', objectUids: [cmd.id] };
    }
    if (cmd instanceof MoveObjectsCommand || cmd instanceof NudgeObjectsCommand) {
      // A nudge is a move with a fixed delta — identical wire representation.
      const serialized: Array<SerializedMove | null> = cmd.moves.map((m) => {
        if (
          !m.from || !m.to ||
          typeof m.from.x !== 'number' || typeof m.from.y !== 'number' ||
          typeof m.to.x !== 'number'   || typeof m.to.y !== 'number'
        ) return null;
        return {
          objectUid: m.id,
          from: { x: m.from.x, y: m.from.y },
          to: { x: m.to.x, y: m.to.y },
        };
      });
      // Reject entire MoveObjectsCommand if any move is malformed. Fail-fast prevents garbage events.
      // Recovery (if needed) is the responsibility of MoveObjectsCommand.apply() — see PRD-010 Finding 7 and ADR-003.
      if (serialized.some((s) => s === null)) return null;
      return { kind: 'move-objects', moves: serialized as SerializedMove[] };
    }
    if (cmd instanceof ResizeObjectCommand) {
      return { kind: 'resize-object', objectUid: cmd.objectId, from: cmd.from, to: cmd.to };
    }
    if (cmd instanceof UpdatePropertyCommand) {
      return {
        kind: 'update-property',
        objectUid: cmd.objectId,
        propertyKey: cmd.propertyKey,
        from: cmd.previousCapturedValue,
        to: cmd.newValue,
      };
    }
    if (cmd instanceof DuplicateObjectsCommand) {
      return {
        kind: 'duplicate-objects',
        sourceUids: extras?.sourceUids ?? [],
        newObjects: cmd.clones,
      };
    }
    if (cmd instanceof ZOrderCommand) {
      return {
        kind: 'z-order',
        objectUid: cmd.objectId,
        operation: cmd.direction,
        fromIndex: cmd.previousIndex,
        toIndex: extras?.toIndex ?? cmd.previousIndex,
      };
    }
    if (cmd instanceof SwapLineCapsCommand) {
      return {
        kind: 'swap-line-caps',
        objectUid: cmd.objectId,
        from: cmd.before,
        to: mirrorLineSwap(cmd.before),
      };
    }
    if (cmd instanceof SetListTypeCommand) {
      // Intentional no-op: SetListTypeCommand is part of the closed
      // WhiteboardCommand union so the exhaustiveness guard remains
      // honest, but it is NOT dispatched as a public event. See file
      // header and PRD-010 Analysis §Deviations.
      return null;
    }
    if (cmd instanceof ApplyWholeTextFormatCommand) {
      // Intentional no-op (like SetListTypeCommand): whole-box text formatting
      // mutates the TipTap document; it is undoable but not yet part of the
      // public serialized-command contract.
      return null;
    }

    // Exhaustiveness guard: TS2322 here when a new variant is added to
    // `WhiteboardCommand` without a matching arm above.
    const _exhaustive: never = cmd;
    void _exhaustive;

    // Runtime fallback for callers who bypass the type system via
    // `as unknown as WhiteboardCommand` or JS interop. DEV-only warn.
    if (import.meta.env?.DEV) {
      console.warn(
        '[CommandSerializer] no serializer for',
        (cmd as { constructor: { name: string } }).constructor.name,
      );
    }
    return null;
  }
}
